import { useCallback, useEffect, useMemo, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import {
    X,
    Search,
    Loader2,
    ChevronLeft,
    ChevronRight,
    DollarSign,
    Bot,
    GitBranch,
    Wrench,
    Zap,
    Sparkles,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { api } from '@/lib/api-client';
import { logger } from '@/lib/telemetry';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface AddFeatureDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    onFeatureAdded: () => void;
    productId: string;
    applicationId: string;
    existingFeatureCapabilityIds: Set<string>;
}

interface ApplicationOption {
    id: string;
    app_key: string;
    name: string | null;
    display_name?: string;
}

interface CapabilityOption {
    id: string;
    name: string;
    fqcn?: string;
    type: string;
    description: string | null;
    visibility?: string;
}

const CAPABILITY_TYPE_GROUPS = ['tool', 'agent', 'workflow'] as const;
const PRODUCT_CAP_PAGE_SIZE = 500;
const TYPE_ICONS: Record<string, typeof Wrench> = {
    tool: Wrench,
    agent: Bot,
    workflow: GitBranch,
};
const TYPE_COLORS: Record<string, string> = {
    tool: 'bg-amber-100 text-amber-700',
    agent: 'bg-blue-100 text-blue-700',
    workflow: 'bg-violet-100 text-violet-700',
};

const PRICING_MODELS: { value: 'flat' | 'metered' | 'per_unit' | 'tiered'; label: string; description: string }[] = [
    { value: 'flat', label: 'Flat', description: 'Fixed recurring price' },
    { value: 'metered', label: 'Metered', description: 'Pay per usage' },
    { value: 'per_unit', label: 'Per Unit', description: 'Price per unit per interval' },
    { value: 'tiered', label: 'Tiered', description: 'Volume-based pricing tiers' },
];

const METRIC_OPTIONS: { value: string; label: string }[] = [
    { value: 'invocations', label: 'Invocations' },
    { value: 'tokens', label: 'Tokens' },
    { value: 'storage_bytes', label: 'Storage (bytes)' },
    { value: 'compute_seconds', label: 'Compute (seconds)' },
    { value: 'messages', label: 'Messages' },
];

const inputClassName =
    'w-full rounded-md border border-slate-200 bg-white py-2 px-3 text-sm font-medium text-slate-700 placeholder-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';

function centsToDisplay(cents: number): string {
    return (cents / 100).toFixed(2);
}
function displayToCents(display: string): number {
    const parsed = parseFloat(display);
    return Number.isNaN(parsed) ? 0 : Math.round(parsed * 100);
}

function parseCapabilityListResponse(data: unknown): CapabilityOption[] {
    const rec = data as { data?: unknown; capabilities?: unknown } | unknown[] | null | undefined;
    const raw = Array.isArray(rec)
        ? rec
        : rec?.data ?? rec?.capabilities ?? [];
    if (!Array.isArray(raw)) return [];
    return raw.filter((c): c is CapabilityOption => {
        if (!c || typeof c !== 'object') return false;
        const cap = c as CapabilityOption;
        return typeof cap.name === 'string' && typeof cap.type === 'string';
    });
}

function dedupeCapabilities(caps: CapabilityOption[]): CapabilityOption[] {
    const seen = new Set<string>();
    const out: CapabilityOption[] = [];
    for (const cap of caps) {
        const key = cap.id || cap.fqcn || `${cap.type}:${cap.name}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(cap);
    }
    return out;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function AddFeatureDrawer({
    isOpen,
    onClose,
    onFeatureAdded,
    productId,
    applicationId,
    existingFeatureCapabilityIds,
}: AddFeatureDrawerProps) {
    const [step, setStep] = useState<'select' | 'pricing'>('select');
    const [submitting, setSubmitting] = useState(false);

    // App resolution
    const [applications, setApplications] = useState<ApplicationOption[]>([]);
    const [loadingApps, setLoadingApps] = useState(false);
    const appKey = useMemo(
        () => applications.find((a) => a.id === applicationId)?.app_key ?? '',
        [applications, applicationId],
    );

    // Capabilities
    const [capabilities, setCapabilities] = useState<CapabilityOption[]>([]);
    const [loadingCaps, setLoadingCaps] = useState(false);
    const [capSearch, setCapSearch] = useState('');
    const [selectedCapIds, setSelectedCapIds] = useState<Set<string>>(new Set());

    // Per-capability pricing (one config per selected capability)
    type FeaturePriceConfig = {
        capability_id: string;
        name: string;
        tier_name: string;
        pricing_model: 'flat' | 'metered' | 'per_unit' | 'tiered';
        amount_cents: number;
        billing_interval: 'month' | 'year';
        usage_limit: number | null;
        overage_unit_amount_cents: number;
        metric: string;
    };
    const [featurePrices, setFeaturePrices] = useState<FeaturePriceConfig[]>([]);

    // -----------------------------------------------------------------------
    // Reset on open
    // -----------------------------------------------------------------------
    useEffect(() => {
        if (isOpen) {
            setStep('select');
            setSubmitting(false);
            setSelectedCapIds(new Set());
            setCapSearch('');
            setFeaturePrices([]);
        }
    }, [isOpen]);

    // Build feature price configs from selected capabilities
    const selectedCaps = useMemo(
        () => capabilities.filter((c) => selectedCapIds.has(c.id)),
        [capabilities, selectedCapIds],
    );

    // -----------------------------------------------------------------------
    // Fetch applications
    // -----------------------------------------------------------------------
    useEffect(() => {
        if (!isOpen) return;
        setLoadingApps(true);
        api.get('/applications', { params: { page: 1, per_page: 100 } })
            .then((res) => {
                const data = res.data?.data ?? res.data?.applications ?? (Array.isArray(res.data) ? res.data : []);
                setApplications(data);
            })
            .catch((err) => {
                logger.error('AddFeatureDrawer:fetchApplications - Failed', { error: err });
                setApplications([]);
            })
            .finally(() => setLoadingApps(false));
    }, [isOpen]);

    // -----------------------------------------------------------------------
    // Fetch capabilities when app_key is known
    // -----------------------------------------------------------------------
    const fetchCapabilities = useCallback(async (key: string) => {
        if (!key) return;
        setLoadingCaps(true);
        try {
            const results = await Promise.all(
                CAPABILITY_TYPE_GROUPS.map((type) =>
                    api.get(`/applications/${key}/capabilities`, {
                        params: { type, per_page: PRODUCT_CAP_PAGE_SIZE, status: 'active' },
                    }),
                ),
            );
            const caps = dedupeCapabilities(
                results.flatMap((res) => parseCapabilityListResponse(res.data)),
            );
            setCapabilities(caps);
        } catch (err) {
            logger.error('AddFeatureDrawer:fetchCapabilities - Failed', { error: err });
            setCapabilities([]);
        } finally {
            setLoadingCaps(false);
        }
    }, []);

    useEffect(() => {
        if (appKey) {
            fetchCapabilities(appKey);
        } else {
            setCapabilities([]);
        }
    }, [appKey, fetchCapabilities]);

    // -----------------------------------------------------------------------
    // Filtered / grouped capabilities (exclude already added)
    // -----------------------------------------------------------------------
    const availableCapabilities = useMemo(
        () => capabilities.filter((c) => !existingFeatureCapabilityIds.has(c.id)),
        [capabilities, existingFeatureCapabilityIds],
    );

    const filteredCapabilities = useMemo(() => {
        const q = capSearch.toLowerCase().trim();
        if (!q) return availableCapabilities;
        return availableCapabilities.filter(
            (c) =>
                c.name.toLowerCase().includes(q) ||
                (c.description?.toLowerCase().includes(q) ?? false) ||
                (c.type?.toLowerCase().includes(q) ?? false),
        );
    }, [availableCapabilities, capSearch]);

    const groupedCapabilities = useMemo(() => {
        const groups: Record<string, CapabilityOption[]> = {};
        for (const cap of filteredCapabilities) {
            const key = CAPABILITY_TYPE_GROUPS.includes(cap.type as (typeof CAPABILITY_TYPE_GROUPS)[number])
                ? cap.type
                : 'other';
            (groups[key] ??= []).push(cap);
        }
        return groups;
    }, [filteredCapabilities]);

    const groupOrder = useMemo(
        () => [...CAPABILITY_TYPE_GROUPS, 'other'] as const,
        [],
    );

    // Sync feature prices when selected capabilities change
    useEffect(() => {
        if (selectedCapIds.size === 0) {
            setFeaturePrices([]);
            return;
        }
        setFeaturePrices((prev) => {
            const existingById = new Map(prev.map((fp) => [fp.capability_id, fp]));
            return selectedCaps.map((cap) => {
                const existing = existingById.get(cap.id);
                if (existing) return existing;
                return {
                    capability_id: cap.id,
                    name: cap.name,
                    tier_name: '',
                    pricing_model: 'flat' as const,
                    amount_cents: 0,
                    billing_interval: 'month' as const,
                    usage_limit: null,
                    overage_unit_amount_cents: 0,
                    metric: 'invocations',
                };
            });
        });
    }, [selectedCapIds, selectedCaps]);

    // -----------------------------------------------------------------------
    // Handlers
    // -----------------------------------------------------------------------
    const toggleCapability = (id: string) => {
        setSelectedCapIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const selectAllVisible = () => {
        setSelectedCapIds((prev) => {
            const next = new Set(prev);
            for (const cap of filteredCapabilities) {
                if (cap.id) next.add(cap.id);
            }
            return next;
        });
    };

    const clearSelection = () => setSelectedCapIds(new Set());

    const handleNext = () => {
        if (selectedCapIds.size > 0) setStep('pricing');
    };

    const handleBack = () => {
        setStep('select');
    };

    const updateFeaturePrice = (capId: string, patch: Partial<FeaturePriceConfig>) => {
        setFeaturePrices((prev) =>
            prev.map((fp) => (fp.capability_id === capId ? { ...fp, ...patch } : fp)),
        );
    };

    const handleSubmit = async () => {
        const toAdd = featurePrices.filter((fp) => fp.pricing_model !== 'tiered');
        const hasTiered = featurePrices.some((fp) => fp.pricing_model === 'tiered');
        if (hasTiered) {
            toast.error('Tiered pricing is not supported. Please change to Flat, Metered, or Per Unit.');
            return;
        }
        if (toAdd.length === 0) return;
        setSubmitting(true);
        try {
            for (const fp of toAdd) {
                await api.post(`/billing/products/${productId}/features`, {
                    capability_id: fp.capability_id,
                    name: fp.name.trim() || fp.name,
                    tier_name: fp.tier_name.trim() || undefined,
                    pricing_model: fp.pricing_model,
                    amount_cents: fp.amount_cents,
                    currency: 'usd',
                    billing_interval: fp.billing_interval,
                    usage_limit: fp.usage_limit ?? undefined,
                    overage_unit_amount_cents:
                        fp.pricing_model === 'metered' || fp.pricing_model === 'per_unit'
                            ? fp.overage_unit_amount_cents
                            : undefined,
                    metric: fp.pricing_model === 'metered' ? fp.metric : undefined,
                    is_base_price: false,
                });
            }
            toast.success(toAdd.length === 1 ? 'Feature added successfully' : `${toAdd.length} features added successfully`);
            onFeatureAdded();
            onClose();
        } catch (err) {
            logger.error('AddFeatureDrawer:handleSubmit - Failed', { error: err });
            toast.error('Failed to add feature(s)');
        } finally {
            setSubmitting(false);
        }
    };

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (
        <SlideDrawer isOpen={isOpen} onClose={onClose}>
                {/* Header */}
                <div className="px-8 py-6 border-b border-slate-100 bg-slate-50/50 shrink-0">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            {step === 'pricing' && (
                                <button
                                    type="button"
                                    onClick={handleBack}
                                    className="rounded-full p-1.5 hover:bg-slate-200 text-slate-600 transition-colors"
                                    aria-label="Back"
                                >
                                    <ChevronLeft size={20} />
                                </button>
                            )}
                            <div>
                                <h2 className="text-xl font-semibold text-slate-900">
                                    {step === 'select' ? 'Add Feature' : 'Configure Pricing'}
                                </h2>
                                <p className="text-sm text-slate-500">
                                    {step === 'select'
                                        ? 'Select capabilities to add to this product'
                                        : `${featurePrices.length} ${featurePrices.length === 1 ? 'feature' : 'features'} selected`}
                                </p>
                            </div>
                        </div>
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-full p-1 hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors"
                            aria-label="Close"
                        >
                            <X size={20} />
                        </button>
                    </div>
                </div>

                {/* Content */}
                <div className="flex-1 overflow-y-auto p-8 bg-white">
                    <AnimatePresence mode="wait">
                        {step === 'select' && (
                            <motion.div
                                key="select"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.2 }}
                                className="space-y-5 max-w-3xl mx-auto"
                            >
                                {loadingApps || !appKey ? (
                                    <div className="space-y-4">
                                        <Skeleton className="h-9 w-64" />
                                        <Skeleton className="h-32 w-full" />
                                    </div>
                                ) : (
                                    <>
                                        {/* Toolbar - matches ProductFormWizard step 2 */}
                                        <div className="flex items-center gap-3">
                                            <div className="relative flex-1">
                                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                                <Input
                                                    placeholder="Search capabilities..."
                                                    value={capSearch}
                                                    onChange={(e) => setCapSearch(e.target.value)}
                                                    className={`${inputClassName} pl-9`}
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={selectAllVisible}
                                                className="text-xs font-medium text-emerald-600 hover:text-emerald-700 whitespace-nowrap"
                                            >
                                                Select All
                                            </button>
                                            <span className="text-slate-300">|</span>
                                            <button
                                                type="button"
                                                onClick={clearSelection}
                                                className="text-xs font-medium text-slate-500 hover:text-slate-700 whitespace-nowrap"
                                            >
                                                Clear
                                            </button>
                                            <Badge variant="secondary" className="ml-auto">
                                                {selectedCapIds.size} selected
                                            </Badge>
                                        </div>

                                        {loadingCaps ? (
                                            <div className="space-y-4">
                                                {[1, 2, 3, 4].map((i) => (
                                                    <div key={i} className="flex items-center gap-3">
                                                        <Skeleton className="h-4 w-4 rounded" />
                                                        <Skeleton className="h-4 w-48" />
                                                    </div>
                                                ))}
                                            </div>
                                        ) : availableCapabilities.length === 0 ? (
                                            <div className="py-12 text-center">
                                                <Sparkles className="mx-auto h-8 w-8 text-slate-300 mb-3" />
                                                <p className="text-sm text-slate-500">
                                                    {capabilities.length === 0
                                                        ? 'No tools, agents, or workflows found for this application. Pick the application that owns the capability you want to sell.'
                                                        : 'All capabilities have already been added to this product.'}
                                                </p>
                                            </div>
                                        ) : filteredCapabilities.length === 0 ? (
                                            <div className="py-12 text-center">
                                                <Sparkles className="mx-auto h-8 w-8 text-slate-300 mb-3" />
                                                <p className="text-sm text-slate-500">No capabilities match your search.</p>
                                            </div>
                                        ) : (
                                            <div className="space-y-6">
                                                {groupOrder.map((groupType) => {
                                                    const caps = groupedCapabilities[groupType];
                                                    if (!caps?.length) return null;
                                                    const Icon = TYPE_ICONS[groupType] ?? Zap;
                                                    const colorCls = TYPE_COLORS[groupType] ?? 'bg-slate-100 text-slate-600';
                                                    const label = groupType === 'other' ? 'Other' : groupType.charAt(0).toUpperCase() + groupType.slice(1) + 's';

                                                    return (
                                                        <div key={groupType}>
                                                            <div className="flex items-center gap-2 mb-3">
                                                                <div className={cn('flex h-6 w-6 items-center justify-center rounded-md', colorCls)}>
                                                                    <Icon size={14} />
                                                                </div>
                                                                <h3 className="text-sm font-semibold text-slate-700">{label}</h3>
                                                                <Badge variant="outline" className="text-[10px]">{caps.length}</Badge>
                                                            </div>
                                                            <div className="grid gap-2">
                                                                {caps.map((cap) => {
                                                                    const selected = !!cap.id && selectedCapIds.has(cap.id);
                                                                    const selectable = !!cap.id;
                                                                    return (
                                                                        <label
                                                                            key={cap.id || cap.fqcn || cap.name}
                                                                            className={cn(
                                                                                'flex items-start gap-3 rounded-lg border p-3 transition-all',
                                                                                !selectable
                                                                                    ? 'cursor-not-allowed opacity-60 border-slate-200'
                                                                                    : selected
                                                                                    ? 'cursor-pointer border-emerald-300 bg-emerald-50/60 shadow-sm'
                                                                                    : 'cursor-pointer border-slate-200 hover:border-slate-300 hover:bg-slate-50/50',
                                                                            )}
                                                                        >
                                                                            <Checkbox
                                                                                checked={selected}
                                                                                disabled={!selectable}
                                                                                onCheckedChange={() => selectable && toggleCapability(cap.id)}
                                                                                className="mt-0.5"
                                                                            />
                                                                            <div className="flex-1 min-w-0">
                                                                                <div className="flex items-center gap-2">
                                                                                    <span className="text-sm font-medium text-slate-800 truncate">
                                                                                        {cap.name}
                                                                                    </span>
                                                                                    <Badge variant="outline" className="text-[10px] shrink-0">
                                                                                        {cap.type}
                                                                                    </Badge>
                                                                                </div>
                                                                                {cap.description && (
                                                                                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">
                                                                                        {cap.description}
                                                                                    </p>
                                                                                )}
                                                                            </div>
                                                                        </label>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}

                                        {!loadingApps && appKey && availableCapabilities.length > 0 && (
                                            <div className="flex justify-end pt-4">
                                                <Button
                                                    onClick={handleNext}
                                                    disabled={selectedCapIds.size === 0}
                                                >
                                                    Next
                                                    <ChevronRight className="h-4 w-4 ml-1.5" />
                                                </Button>
                                            </div>
                                        )}
                                    </>
                                )}
                            </motion.div>
                        )}

                        {step === 'pricing' && featurePrices.length > 0 && (
                            <motion.div
                                key="pricing"
                                initial={{ opacity: 0, x: 20 }}
                                animate={{ opacity: 1, x: 0 }}
                                exit={{ opacity: 0, x: -20 }}
                                transition={{ duration: 0.2 }}
                                className="space-y-8 max-w-3xl mx-auto"
                            >
                                {/* Per-Capability Pricing - matches ProductFormWizard step 3 */}
                                <section className="space-y-4">
                                    <h3 className="text-base font-semibold text-slate-800">
                                        Capability Pricing
                                        <span className="ml-2 text-sm font-normal text-slate-500">
                                            ({featurePrices.length} {featurePrices.length === 1 ? 'feature' : 'features'})
                                        </span>
                                    </h3>

                                    <div className="space-y-4">
                                        {featurePrices.map((fp) => (
                                            <div
                                                key={fp.capability_id}
                                                className={cn(
                                                    'rounded-lg border p-5 space-y-4 transition-colors',
                                                    'border-slate-200 bg-slate-50/30',
                                                )}
                                            >
                                                {/* Card header */}
                                                <div className="flex items-center gap-3">
                                                    <div className="flex h-8 w-8 items-center justify-center rounded-md bg-slate-100">
                                                        <Zap size={14} className="text-slate-600" />
                                                    </div>
                                                    <div className="flex-1 min-w-0">
                                                        <Input
                                                            value={fp.name}
                                                            onChange={(e) => updateFeaturePrice(fp.capability_id, { name: e.target.value })}
                                                            className="text-sm font-semibold border-0 bg-transparent p-0 h-auto shadow-none focus:ring-0 focus:outline-none"
                                                            placeholder="Feature name"
                                                        />
                                                    </div>
                                                </div>

                                                {/* Tier name (optional) */}
                                                <div className="space-y-1.5">
                                                    <Label className="text-xs">Tier Name (optional)</Label>
                                                    <Input
                                                        value={fp.tier_name}
                                                        onChange={(e) => updateFeaturePrice(fp.capability_id, { tier_name: e.target.value })}
                                                        placeholder="e.g. premium, basic"
                                                        className={inputClassName}
                                                    />
                                                </div>

                                                {/* Pricing model selector */}
                                                <div className="grid grid-cols-4 gap-2">
                                                    {PRICING_MODELS.map((pm) => (
                                                        <button
                                                            key={pm.value}
                                                            type="button"
                                                            onClick={() => updateFeaturePrice(fp.capability_id, { pricing_model: pm.value })}
                                                            className={cn(
                                                                'rounded-md border px-3 py-2 text-left transition-all',
                                                                fp.pricing_model === pm.value
                                                                    ? 'border-emerald-400 bg-emerald-50 shadow-sm'
                                                                    : 'border-slate-200 hover:border-slate-300 bg-white',
                                                            )}
                                                        >
                                                            <span
                                                                className={cn(
                                                                    'block text-xs font-semibold',
                                                                    fp.pricing_model === pm.value ? 'text-emerald-700' : 'text-slate-700',
                                                                )}
                                                            >
                                                                {pm.label}
                                                            </span>
                                                            <span className="block text-[10px] text-slate-400 mt-0.5">
                                                                {pm.description}
                                                            </span>
                                                        </button>
                                                    ))}
                                                </div>

                                                {/* Flat / Per Unit fields */}
                                                {(fp.pricing_model === 'flat' || fp.pricing_model === 'per_unit') && (
                                                    <div className="grid grid-cols-2 gap-4">
                                                        <div className="space-y-1.5">
                                                            <Label className="text-xs">
                                                                {fp.pricing_model === 'flat' ? 'Amount ($)' : 'Unit Price ($)'}
                                                            </Label>
                                                            <div className="relative">
                                                                <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                                                                <Input
                                                                    type="number"
                                                                    step="0.01"
                                                                    min={0}
                                                                    placeholder="0.00"
                                                                    value={fp.amount_cents ? centsToDisplay(fp.amount_cents) : ''}
                                                                    onChange={(e) =>
                                                                        updateFeaturePrice(fp.capability_id, {
                                                                            amount_cents: displayToCents(e.target.value),
                                                                        })
                                                                    }
                                                                    className={`${inputClassName} pl-8`}
                                                                />
                                                            </div>
                                                        </div>
                                                        <div className="space-y-1.5">
                                                            <Label className="text-xs">Interval</Label>
                                                            <Select
                                                                value={fp.billing_interval}
                                                                onValueChange={(v) =>
                                                                    updateFeaturePrice(fp.capability_id, {
                                                                        billing_interval: v as 'month' | 'year',
                                                                    })
                                                                }
                                                            >
                                                                <SelectTrigger className="border-slate-200">
                                                                    <SelectValue />
                                                                </SelectTrigger>
                                                                <SelectContent className="z-[250]" position="popper">
                                                                    <SelectItem value="month">Monthly</SelectItem>
                                                                    <SelectItem value="year">Yearly</SelectItem>
                                                                </SelectContent>
                                                            </Select>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Metered fields */}
                                                {fp.pricing_model === 'metered' && (
                                                    <div className="space-y-4">
                                                        <div className="grid grid-cols-2 gap-4">
                                                            <div className="space-y-1.5">
                                                                <Label className="text-xs">Unit Amount ($)</Label>
                                                                <div className="relative">
                                                                    <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                                                                    <Input
                                                                        type="number"
                                                                        step="0.0001"
                                                                        min={0}
                                                                        placeholder="0.0000"
                                                                        value={fp.amount_cents ? centsToDisplay(fp.amount_cents) : ''}
                                                                        onChange={(e) =>
                                                                            updateFeaturePrice(fp.capability_id, {
                                                                                amount_cents: displayToCents(e.target.value),
                                                                            })
                                                                        }
                                                                        className={`${inputClassName} pl-8`}
                                                                    />
                                                                </div>
                                                            </div>
                                                            <div className="space-y-1.5">
                                                                <Label className="text-xs">Metric</Label>
                                                                <Select
                                                                    value={fp.metric}
                                                                    onValueChange={(v) =>
                                                                        updateFeaturePrice(fp.capability_id, { metric: v })
                                                                    }
                                                                >
                                                                    <SelectTrigger className="border-slate-200">
                                                                        <SelectValue />
                                                                    </SelectTrigger>
                                                                    <SelectContent className="z-[250]" position="popper">
                                                                        {METRIC_OPTIONS.map((m) => (
                                                                            <SelectItem key={m.value} value={m.value}>
                                                                                {m.label}
                                                                            </SelectItem>
                                                                        ))}
                                                                    </SelectContent>
                                                                </Select>
                                                            </div>
                                                        </div>
                                                        <div className="grid grid-cols-2 gap-4">
                                                            <div className="space-y-1.5">
                                                                <Label className="text-xs">Usage Limit</Label>
                                                                <Input
                                                                    type="number"
                                                                    min={0}
                                                                    placeholder="Unlimited"
                                                                    value={fp.usage_limit ?? ''}
                                                                    onChange={(e) =>
                                                                        updateFeaturePrice(fp.capability_id, {
                                                                            usage_limit: e.target.value
                                                                                ? parseInt(e.target.value) || 0
                                                                                : null,
                                                                        })
                                                                    }
                                                                    className={inputClassName}
                                                                />
                                                                <p className="text-[10px] text-slate-400">Leave empty for unlimited</p>
                                                            </div>
                                                            <div className="space-y-1.5">
                                                                <Label className="text-xs">Overage Rate ($)</Label>
                                                                <div className="relative">
                                                                    <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                                                                    <Input
                                                                        type="number"
                                                                        step="0.0001"
                                                                        min={0}
                                                                        placeholder="0.0000"
                                                                        value={
                                                                            fp.overage_unit_amount_cents
                                                                                ? centsToDisplay(fp.overage_unit_amount_cents)
                                                                                : ''
                                                                        }
                                                                        onChange={(e) =>
                                                                            updateFeaturePrice(fp.capability_id, {
                                                                                overage_unit_amount_cents: displayToCents(e.target.value),
                                                                            })
                                                                        }
                                                                        className={`${inputClassName} pl-8`}
                                                                    />
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}

                                                {/* Tiered - not supported by API */}
                                                {fp.pricing_model === 'tiered' && (
                                                    <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-4">
                                                        <p className="text-sm text-amber-800">
                                                            Tiered pricing is not supported when adding features. Please choose Flat, Metered, or Per Unit.
                                                        </p>
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </section>

                                <div className="flex gap-3">
                                    <Button variant="outline" onClick={handleBack}>
                                        Back
                                    </Button>
                                    <Button
                                        onClick={handleSubmit}
                                        disabled={
                                            submitting ||
                                            featurePrices.some((fp) => fp.pricing_model === 'tiered')
                                        }
                                    >
                                        {submitting ? (
                                            <>
                                                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                                Adding...
                                            </>
                                        ) : (
                                            `Add ${featurePrices.filter((fp) => fp.pricing_model !== 'tiered').length} Feature(s)`
                                        )}
                                    </Button>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>
        </SlideDrawer>
    );
}
